import { CalendarClock, CarFront, Lock, Clock3, KeyRound, Hotel, Phone, Users, Briefcase } from "lucide-react";
import { formatEuro, formatPickup } from "@/lib/api";
import { WhatsAppButton } from "@/components/fleet/WhatsAppButton";
import { RideFlags, isSos } from "@/components/fleet/RideFlags";
import { RatingBadge } from "@/components/fleet/Stars";
import { t } from "@/lib/i18n";
import { RouteInfo } from "@/components/fleet/RouteInfo";

export const VerifiedBadge = ({ testid = "ride-verified-badge" }) => (
  <span className="ff-badge ff-badge-verified text-xs" data-testid={testid}>{t("NCC Verificato 🛡️")}</span>
);

export const RouteBlock = ({ ride }) => (
  <div data-testid="ride-route" className={`flex flex-col gap-1 ${ride.locked ? "ff-blur-link" : ""}`}>
    <div className="flex items-center gap-2.5 text-white font-extrabold text-2xl leading-tight">
      <span aria-hidden="true" className="text-xl">📍</span>
      <span data-testid="ride-origin">{ride.origin}</span>
    </div>
    <div className="pl-2 text-[#10B981] text-lg font-bold leading-none">➔</div>
    <div className="flex items-center gap-2.5 text-white font-extrabold text-2xl leading-tight">
      <span aria-hidden="true" className="text-xl">🏁</span>
      <span data-testid="ride-destination">{ride.destination}</span>
    </div>
  </div>
);

export const RideMeta = ({ ride }) => {
  const { date, time } = formatPickup(ride.pickup_at);
  return (
    <div className="flex flex-col gap-2">
      <div data-testid="ride-datetime" className="flex items-center gap-2 text-lg font-bold text-white">
        <CalendarClock size={20} className="text-[#94A3B8]" /> <span className="capitalize">{date}</span> <span className="text-[#94A3B8]">•</span> {time}
      </div>
      <div data-testid="ride-vehicle" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#94A3B8]">
        <span className="flex items-center gap-2"><CarFront size={17} /> {t(ride.vehicle_category)}</span>
        <span data-testid="ride-passengers" className="flex items-center gap-1"><Users size={15} /> {ride.passengers || 1} pax</span>
        <span data-testid="ride-luggage" className="flex items-center gap-1"><Briefcase size={15} /> {ride.luggage || 0} {t("valigie")}</span>
      </div>
      <RouteInfo km={ride.distance_km} min={ride.duration_min} testid={`ride-route-${ride.id}`} />
    </div>
  );
};

const CardAction = ({ ride, onBook, onUnlock }) => {
  if (ride.is_mine) {
    return (
      <div data-testid="ride-card-own-label" className="h-14 rounded-xl border border-dashed border-[#3f3f46] flex items-center justify-center gap-2 text-sm font-semibold text-[#94A3B8]">
        <Clock3 size={16} /> {t("La tua corsa • in attesa di un collega")}
      </div>
    );
  }
  if (ride.locked) {
    return (
      <button type="button" data-testid={`ride-card-unlock-button-${ride.id}`} onClick={onUnlock} className="ff-btn-secondary w-full text-sm uppercase border-[#3B82F6]">
        <KeyRound size={17} /> {t("Accedi per vedere e prenotare")}
      </button>
    );
  }
  return (
    <button type="button" data-testid={`ride-card-book-button-${ride.id}`} onClick={() => onBook(ride)} className="ff-btn-primary w-full text-sm uppercase">
      <Lock size={17} strokeWidth={2.5} /> {t("PRENOTA E BLOCCA (Escrow 100% Sicuro)")}
    </button>
  );
};

export const ContactInfo = ({ contact }) => (
  <div data-testid="ride-contact" className="rounded-xl border border-[#3B82F6]/40 bg-[#3B82F6]/10 p-3 flex flex-col gap-1 text-sm">
    <span className="font-bold text-white">{contact.guest_name} • {contact.passengers} pers.</span>
    <a href={`tel:${contact.contact_phone}`} className="flex items-center gap-2 text-[#93C5FD] font-semibold"><Phone size={15} /> {contact.contact_phone}</a>
    {contact.notes && <span className="text-[#94A3B8]">{contact.notes}</span>}
  </div>
);

export const RideCard = ({ ride, index = 0, onBook, onUnlock, hideShare }) => (
  <article data-testid={`ride-card-${ride.id}`} data-sos={isSos(ride)} className={`ff-card ff-card-hover ff-rise p-5 flex flex-col gap-5 ${isSos(ride) ? "ff-sos-card" : ""}`} style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}>
    <RideFlags ride={ride} />
    <div className="flex items-center justify-between gap-2">
      <span className="ff-badge ff-badge-green text-xs uppercase tracking-wide" data-testid="ride-service-type">{t(ride.service_type)}</span>
      {ride.source === "concierge" ? (
        <span className="ff-badge ff-badge-cobalt text-xs" data-testid="ride-concierge-badge"><Hotel size={13} /> {t("Richiesta Hotel")}</span>
      ) : (
        ride.publisher_verified && <VerifiedBadge />
      )}
    </div>
    <RouteBlock ride={ride} />
    <RideMeta ride={ride} />
    <div className="flex items-end justify-between gap-3 pt-4 border-t border-[#27272A]">
      <div>
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">{t("Tariffa totale")}</span>
        <div data-testid="ride-price" className="ff-display text-4xl font-extrabold text-white leading-none mt-1">{formatEuro(ride.fare)}</div>
      </div>
      <span className="text-xs text-[#94A3B8] text-right leading-tight">{t("Ceduta da")}<br /><span className="text-white font-semibold">{ride.publisher_name}</span><br /><RatingBadge rating={ride.publisher_rating} testid={`ride-rating-${ride.id}`} /></span>
    </div>
    {ride.contact && <ContactInfo contact={ride.contact} />}
    <div className="flex flex-col gap-2.5">
      <CardAction ride={ride} onBook={onBook} onUnlock={onUnlock} />
      {!hideShare && <WhatsAppButton ride={ride} testid={`ride-card-whatsapp-button-${ride.id}`} />}
    </div>
  </article>
);
